// components/TemporaryCustomerListModal.tsx
// 仮登録顧客一覧モーダル（本実装）
// /api2/customercompare/repage で一覧取得、/api2/customercompare/select で明細取得
// 編集ボタンで明細を取得し scanResult を組み立てて /edit へ遷移（本登録モード）

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, /* Checkbox, */ DatePicker, Switch, message, Spin } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  ZoomInOutlined,
  FilterOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  ReloadOutlined,
  PictureOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { getRoutePath } from '../config';
import { businessCardAPI } from '../services/api';

// 「ログインユーザが登録した顧客に絞り込む」チェックボックスの状態を保持する localStorage キー
const STORAGE_KEY_SELF_ONLY = 'temp_customer_list_self_only';
const readSelfOnlyFromStorage = (): boolean => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_SELF_ONLY);
    if (saved === null) return true; // 未保存時は初期状態 ON
    return saved === 'true';
  } catch {
    return true;
  }
};
const writeSelfOnlyToStorage = (val: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY_SELF_ONLY, String(val));
  } catch {
    // 例: ストレージ無効・容量超過などは無視
  }
};

// 名刺画像表示トグルの状態を保持する localStorage キー（BusinessCardListPage とは別キー）
const STORAGE_KEY_SHOW_IMAGES = 'temp_customer_list_show_images';
const readShowImagesFromStorage = (): boolean => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_SHOW_IMAGES);
    return saved === 'true'; // 未保存時は OFF
  } catch {
    return false;
  }
};
const writeShowImagesToStorage = (val: boolean) => {
  try {
    localStorage.setItem(STORAGE_KEY_SHOW_IMAGES, String(val));
  } catch {
    // ignore
  }
};

// 仮登録顧客一覧の1件（/api2/customercompare/repage の customertemplist 要素）
type TempCustomerListItem = {
  batchcreatetype: number;
  batchcreateno: number;
  createfunction: number;
  startdate: string; // ISO timestamp
  customerno: number;
  companyname: string;
  deptname: string;
  customername: string;
  userno: number;
  username: string;
};

// No Photo 用 SVG data URI（薄いグレー）
const NO_PHOTO_IMAGE: string = (() => {
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='200' height='120' viewBox='0 0 200 120'>
    <rect width='200' height='120' fill='#f0f0f0' stroke='#d9d9d9' stroke-width='1'/>
    <text x='100' y='68' font-size='16' fill='#bfbfbf' text-anchor='middle' font-family='sans-serif'>No Photo</text>
  </svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
})();

interface TemporaryCustomerListModalProps {
  open: boolean;
  onClose: () => void;
  /** 件数が変動したときに親（通知バッジ）へ通知するコールバック */
  onCountChange?: (count: number) => void;
}

const TemporaryCustomerListModal: React.FC<TemporaryCustomerListModalProps> = ({ open, onClose, onCountChange }) => {
  const navigate = useNavigate();

  // 一覧データ
  const [customers, setCustomers] = useState<TempCustomerListItem[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  // 絞り込み条件パネル（編集中の値）
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  // 「ログインユーザが登録した顧客に絞り込む」チェック（localStorage から初期化、未保存時は ON）
  const [filterSelfOnly, setFilterSelfOnly] = useState<boolean>(() => readSelfOnlyFromStorage());
  const [filterStartDate, setFilterStartDate] = useState<Dayjs | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Dayjs | null>(null);
  // 「絞り込み」適用後の条件（初期も localStorage から）
  const [appliedFilter, setAppliedFilter] = useState<{
    selfOnly: boolean;
    startDate?: string;
    endDate?: string;
  }>(() => ({ selfOnly: readSelfOnlyFromStorage() }));

  // サムネ拡大プレビュー
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  // 削除確認モーダル
  const [deleteTarget, setDeleteTarget] = useState<TempCustomerListItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);
  // 編集処理中（/select 呼び出し中）の customerno（押した行だけローディング表示するため）
  const [editingNo, setEditingNo] = useState<number | null>(null);

  // 名刺画像表示トグル（localStorage で永続化）
  const [showCardImages, setShowCardImages] = useState<boolean>(() => readShowImagesFromStorage());
  // customerno → 画像 base64 / URL（null=取得済みだが画像無し、undefined=未取得）
  const [customerImages, setCustomerImages] = useState<{ [no: number]: string | null }>({});
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  // クロージャ内から「現在の」値を参照するための ref
  const showCardImagesRef = useRef<boolean>(showCardImages);
  const customerImagesRef = useRef<{ [no: number]: string | null }>(customerImages);
  const loadingImagesRef = useRef<boolean>(false);
  useEffect(() => {
    showCardImagesRef.current = showCardImages;
    writeShowImagesToStorage(showCardImages);
  }, [showCardImages]);
  useEffect(() => {
    customerImagesRef.current = customerImages;
  }, [customerImages]);

  // 名刺画像を順次取得（5件ずつバッチ、バッチごとに state を更新して逐次表示）
  const fetchCustomerImages = useCallback(async (targets: TempCustomerListItem[]) => {
    if (loadingImagesRef.current) return;
    if (targets.length === 0) return;
    const toFetch = targets.filter(
      (c) => customerImagesRef.current[c.customerno] === undefined
    );
    if (toFetch.length === 0) return;

    loadingImagesRef.current = true;
    setLoadingImages(true);
    try {
      const batchSize = 5;
      for (let i = 0; i < toFetch.length; i += batchSize) {
        const batch = toFetch.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(async (c) => {
            try {
              const res = await businessCardAPI.selectTemporaryCustomer(c.customerno);
              if (res?.statuscode === 200 && res.customerdata) {
                return {
                  no: c.customerno,
                  img: (res.customerdata.businesscardfrontimage as string | null) ?? null,
                };
              }
            } catch (e) {
              console.error(`仮登録顧客 ${c.customerno} の画像取得エラー:`, e);
            }
            return { no: c.customerno, img: null };
          })
        );
        // バッチごとに反映（順次表示）
        setCustomerImages((prev) => {
          const next = { ...prev };
          results.forEach((r) => {
            next[r.no] = r.img;
          });
          return next;
        });
        // トグルが OFF になったら以降のバッチを中断
        if (!showCardImagesRef.current) break;
      }
    } finally {
      loadingImagesRef.current = false;
      setLoadingImages(false);
    }
  }, []);

  // トグル ON または customers 変更時に画像取得をキック
  useEffect(() => {
    if (!showCardImages) return;
    if (customers.length === 0) return;
    fetchCustomerImages(customers);
  }, [showCardImages, customers, fetchCustomerImages]);

  // 一覧取得（appliedFilter を参照）。
  // 「ログインユーザが登録した顧客に絞り込む」が ON → isAllUser=false（サーバ側で絞り込み）
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await businessCardAPI.searchTemporaryCustomers({
        // 仕様: 仮登録一覧は常にログインユーザ登録分のみ表示するため isAllUser は常に false。
        // （絞り込みチェックボックスは今後の為コメントアウトで残置。元: isAllUser: !appliedFilter.selfOnly）
        isAllUser: false,
        searchCreatefunction: 9, // UP-TONE からの仮登録のみを対象に検索
        page: 1,
        viewCount: 100,
        sortKey: 0, // 登録日時
        sortOrder: false, // 降順
      });
      if (res?.statuscode === 200 && Array.isArray(res.customertemplist)) {
        setCustomers(res.customertemplist as TempCustomerListItem[]);
        const newTotal = typeof res.customertemplistsize === 'number' ? res.customertemplistsize : res.customertemplist.length;
        setTotalCount(newTotal);
        onCountChange?.(newTotal);
      } else {
        // 取得失敗時は親バッジを上書きせず、モーダル内表示のみ 0 にする
        setCustomers([]);
        setTotalCount(0);
        if (res?.message) message.error(res.message);
      }
    } catch (e) {
      console.error('仮登録顧客一覧取得エラー:', e);
      // 通信エラー時は親バッジを上書きせず、モーダル内表示のみ 0 にする
      setCustomers([]);
      setTotalCount(0);
      message.error('仮登録顧客一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [appliedFilter.selfOnly, onCountChange]);

  // モーダル open / 絞り込み適用変更時に再取得
  useEffect(() => {
    if (!open) return;
    fetchList();
  }, [open, fetchList]);

  // 開いた瞬間に絞り込みパネル等を初期化
  // 「自分のみ絞り込む」は localStorage の最終状態を採用（未保存時は ON）
  useEffect(() => {
    if (!open) return;
    const selfOnly = readSelfOnlyFromStorage();
    setFiltersOpen(false);
    setFilterSelfOnly(selfOnly);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setAppliedFilter({ selfOnly });
    setEnlargedImage(null);
    setDeleteTarget(null);
  }, [open]);

  // 取込日（startdate）から YYYY-MM-DD を抽出
  const startdateToDay = (iso: string): string => {
    if (!iso) return '';
    return dayjs(iso).format('YYYY-MM-DD');
  };

  // クライアント側フィルタ（取込日範囲のみ。担当者は isAllUser でサーバ側に委譲）
  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      if (appliedFilter.startDate || appliedFilter.endDate) {
        const day = startdateToDay(c.startdate);
        if (appliedFilter.startDate && day < appliedFilter.startDate) return false;
        if (appliedFilter.endDate && day > appliedFilter.endDate) return false;
      }
      return true;
    });
  }, [customers, appliedFilter]);

  // 日付（取込日）ごとにグループ化（新しい順）
  const grouped = useMemo(() => {
    const map = new Map<string, TempCustomerListItem[]>();
    for (const c of filteredCustomers) {
      const day = startdateToDay(c.startdate);
      const arr = map.get(day) || [];
      arr.push(c);
      map.set(day, arr);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [filteredCustomers]);

  const handleApplyFilter = () => {
    // 「自分のみ絞り込む」状態を localStorage に保存
    writeSelfOnlyToStorage(filterSelfOnly);
    setAppliedFilter({
      selfOnly: filterSelfOnly,
      startDate: filterStartDate?.format('YYYY-MM-DD'),
      endDate: filterEndDate?.format('YYYY-MM-DD'),
    });
  };

  const handleClearFilter = () => {
    // 初期状態（自分のみ=ON、日付なし）に戻し、即座に適用
    setFilterSelfOnly(true);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setAppliedFilter({ selfOnly: true });
    writeSelfOnlyToStorage(true);
  };

  // 仮登録顧客の削除（/api2/customercompare/delete に searchCustomerno を指定して送信）
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      const res = await businessCardAPI.deleteTemporaryCustomer(deleteTarget.customerno);
      if (res?.statuscode === 200) {
        setCustomers((prev) => prev.filter((c) => c.customerno !== deleteTarget.customerno));
        const nextCount = Math.max(0, totalCount - 1);
        setTotalCount(nextCount);
        onCountChange?.(nextCount);
        setDeleteTarget(null);
        message.success('削除しました');
      } else {
        message.error(res?.message || '削除に失敗しました');
      }
    } catch (e) {
      console.error('削除エラー:', e);
      message.error('削除に失敗しました');
    } finally {
      setDeleteLoading(false);
    }
  };

  // 編集ボタン: /select で明細取得 → scanResult を組み立て /edit へ
  const handleEdit = async (c: TempCustomerListItem) => {
    if (editingNo !== null) return; // 連打・他行同時押し防止
    setEditingNo(c.customerno);
    try {
      const detail = await businessCardAPI.selectTemporaryCustomer(c.customerno);
      if (detail?.statuscode !== 200 || !detail.customerdata) {
        message.error(detail?.message || '仮登録顧客の明細取得に失敗しました');
        return;
      }
      const customerdata = detail.customerdata;
      const duplicatedata = detail.duplicatedata; // 重複が無い場合は未返却

      const frontImage = customerdata.businesscardfrontimage || NO_PHOTO_IMAGE;
      // EditResultPage が customerdata から既存編集モードでフォームを復元できるよう apiResponse に格納
      const scanResult = {
        success: true,
        data: customerdata, // フォールバック用に同じデータを保持
        imageUrl: frontImage,
        apiResponse: {
          customerdata,
          duplicatedata: duplicatedata || undefined,
          duplicatelist: duplicatedata ? [duplicatedata] : [],
        },
      };

      // 本登録モードのフラグ群を IndexedDB へ
      await indexedDBStorage.setItem('permanentRegistrationMode', 'true');
      await indexedDBStorage.setItem('scanResult', JSON.stringify(scanResult));
      await indexedDBStorage.removeItem('editResultBackup');
      // 新規本登録扱い: 既存実顧客の編集ではないので editingCustomerNo はクリア
      await indexedDBStorage.removeItem('editingCustomerNo');

      // 「本登録して次へ」用に、絞り込み後リストと現在の customerno を保存
      // EditResultPage が submit 後に次の仮登録顧客を順次切り替えるために参照する
      await indexedDBStorage.setItem(
        'tempRegistrationList',
        JSON.stringify(filteredCustomers)
      );
      await indexedDBStorage.setItem(
        'tempRegistrationCurrentNo',
        String(c.customerno)
      );
      // 取込日の絞り込み条件も保存する。regist API は日付条件を持たないため、
      // 日付指定時は EditResultPage 側で（日付フィルタ済みの）このリスト順に「次へ」を進める。
      await indexedDBStorage.setItem(
        'tempRegistrationDateFilter',
        JSON.stringify({
          startDate: appliedFilter.startDate ?? null,
          endDate: appliedFilter.endDate ?? null,
        })
      );

      onClose();
      navigate(getRoutePath('/edit'));
    } catch (e) {
      console.error('編集遷移エラー:', e);
      message.error('編集画面の準備に失敗しました');
    } finally {
      setEditingNo(null);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        title={null}
        width={680}
        centered
        styles={{ body: { padding: 0 } }}
      >
        {/* ヘッダ */}
        <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #f0f0f0' }}>
          <div style={{ fontWeight: 'bold', fontSize: 16 }}>仮登録顧客</div>
          {/* 絞り込み条件ボタンと件数を同じ段で表示 */}
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <Button
              size="small"
              onClick={() => setFiltersOpen(!filtersOpen)}
              icon={<FilterOutlined />}
            >
              絞り込み条件 {filtersOpen ? <CaretUpOutlined /> : <CaretDownOutlined />}
            </Button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 'bold', color: '#262626' }}>
                全 {filteredCustomers.length} 件
              </div>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={fetchList}
                loading={loading}
                aria-label="更新"
              />
            </div>
          </div>
          {/* 名刺画像表示トグル（顧客一覧と同様。ON 時は明細APIから順次取得） */}
          <div
            style={{
              marginTop: 6,
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <PictureOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontSize: 13, color: '#595959' }}>名刺画像表示:</span>
            <Switch
              size="small"
              checkedChildren={<EyeOutlined />}
              unCheckedChildren={<EyeInvisibleOutlined />}
              checked={showCardImages}
              onChange={(checked) => setShowCardImages(checked)}
            />
            {showCardImages && loadingImages && (
              <Spin size="small" style={{ marginLeft: 4 }} />
            )}
          </div>
          {filtersOpen && (
            <div
              style={{
                marginTop: 10,
                padding: 12,
                backgroundColor: '#fafafa',
                border: '1px solid #f0f0f0',
                borderRadius: 6,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              {/* 今後の為に残置。仕様により isAllUser を常に false（自分の登録分のみ）にしたため非表示。
              <div>
                <Checkbox
                  checked={filterSelfOnly}
                  onChange={(e) => setFilterSelfOnly(e.target.checked)}
                >
                  ログインユーザが登録した顧客に絞り込む
                </Checkbox>
              </div>
              */}
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 4, color: '#595959' }}>取込日（開始）</div>
                  <DatePicker
                    value={filterStartDate}
                    onChange={setFilterStartDate}
                    style={{ width: '100%' }}
                    placeholder="開始日"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, marginBottom: 4, color: '#595959' }}>取込日（終了）</div>
                  <DatePicker
                    value={filterEndDate}
                    onChange={setFilterEndDate}
                    style={{ width: '100%' }}
                    placeholder="終了日"
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button onClick={handleClearFilter}>クリア</Button>
                <Button type="primary" onClick={handleApplyFilter}>絞り込み</Button>
              </div>
            </div>
          )}
        </div>

        {/* 一覧本体（日付ごとにグループ化） */}
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '8px 16px 16px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin />
            </div>
          ) : grouped.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', padding: 32 }}>
              現在の条件に該当する仮登録顧客はありません
            </div>
          ) : (
            grouped.map(([date, list]) => (
              <div key={date} style={{ marginBottom: 16 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 'bold',
                    color: '#595959',
                    padding: '6px 0',
                    borderBottom: '2px solid #0693e3',
                    marginBottom: 8,
                  }}
                >
                  {dayjs(date).format('YYYY/MM/DD')}（{list.length}件）
                </div>
                {list.map((c) => (
                  <div
                    key={c.customerno}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                      padding: 10,
                      border: '1px solid #f0f0f0',
                      borderRadius: 6,
                      marginBottom: 8,
                      backgroundColor: '#fff',
                    }}
                  >
                    {/* 上段: 名刺情報（サムネ + 各情報） */}
                    <div style={{ display: 'flex', gap: 12 }}>
                      {/* サムネ: トグル ON のときのみプレビュー枠を表示 */}
                      {showCardImages && (() => {
                        const loaded = customerImages[c.customerno];
                        const imgSrc = loaded ? loaded : NO_PHOTO_IMAGE;
                        const isLoadingThis =
                          loadingImages && customerImages[c.customerno] === undefined;
                        return (
                          <div style={{ position: 'relative', flexShrink: 0 }}>
                            <img
                              src={imgSrc}
                              alt={c.customername}
                              onClick={() => setEnlargedImage(imgSrc)}
                              style={{
                                width: 100,
                                height: 60,
                                objectFit: 'cover',
                                borderRadius: 4,
                                cursor: 'pointer',
                                border: '1px solid #d9d9d9',
                                display: 'block',
                              }}
                            />
                            {isLoadingThis && (
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  background: 'rgba(255,255,255,0.6)',
                                  borderRadius: 4,
                                }}
                              >
                                <Spin size="small" />
                              </div>
                            )}
                            <ZoomInOutlined
                              style={{
                                position: 'absolute',
                                bottom: 2,
                                right: 2,
                                color: '#fff',
                                background: 'rgba(0,0,0,0.5)',
                                padding: 3,
                                borderRadius: 3,
                                fontSize: 10,
                                pointerEvents: 'none',
                              }}
                            />
                          </div>
                        );
                      })()}
                      {/* 情報 */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 2 }}>
                          {c.customername || '(顧客名未設定)'}
                        </div>
                        <div style={{ fontSize: 12, color: '#595959', marginBottom: 2 }}>
                          {c.companyname || '-'}
                          {c.deptname ? `／${c.deptname}` : ''}
                        </div>
                      </div>
                    </div>
                    {/* 下段: 操作ボタン（横並び） */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        justifyContent: 'flex-end',
                        borderTop: '1px dashed #f0f0f0',
                        paddingTop: 8,
                      }}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => setDeleteTarget(c)}
                      >
                        削除
                      </Button>
                      <Button
                        size="small"
                        type="primary"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(c)}
                        loading={editingNo === c.customerno}
                        // 他行を処理中は色を変えず（disabled にせず）、クリックだけ無効化する
                        style={
                          editingNo !== null && editingNo !== c.customerno
                            ? { pointerEvents: 'none' }
                            : undefined
                        }
                      >
                        編集
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      </Modal>

      {/* サムネ拡大モーダル */}
      <Modal
        open={enlargedImage !== null}
        onCancel={() => setEnlargedImage(null)}
        footer={null}
        centered
        width="92vw"
        styles={{ body: { padding: 0, backgroundColor: '#000' } }}
        destroyOnClose
      >
        {enlargedImage && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
            }}
          >
            <img
              src={enlargedImage}
              alt="名刺画像"
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          </div>
        )}
      </Modal>

      {/* 削除確認モーダル */}
      <Modal
        open={deleteTarget !== null}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleDeleteConfirm}
        okText="削除"
        cancelText="キャンセル"
        okButtonProps={{ danger: true, loading: deleteLoading }}
        cancelButtonProps={{ disabled: deleteLoading }}
        closable={!deleteLoading}
        maskClosable={!deleteLoading}
        title="削除確認"
        centered
      >
        {deleteTarget && (
          <div>
            <p>以下の仮登録顧客を削除します。よろしいですか？</p>
            <p style={{ fontWeight: 'bold' }}>
              {deleteTarget.customername}（{deleteTarget.companyname}）
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};

export default TemporaryCustomerListModal;
